// Lazy loader for the widget components.
//
// The manifests live in manifest.js because metadata is cheap and nearly
// everything wants it. The components live behind a dynamic import because
// they are not cheap and a page only ever renders one of them. Before this
// split, a Tweet Grid embed downloaded Stories, Video Reels, Audio Wall and
// 23 other components in order to render one grid, and the kind picker
// pulled all 27 in purely to list their names.
//
// To add a widget: append its manifest to MANIFESTS in manifest.js and its
// file to LOADERS below. Both are keyed by the same string, which is also
// Widget#kind in the database.

import { useEffect, useState } from "react";

// The argument to import() has to be a literal path. Vite reads these
// statically to decide what to split into chunks, so a computed path would
// leave us with no chunks at all.
const LOADERS = {
  "Masonry":            () => import("./Masonry"),
  "Quote Grid":         () => import("./QuoteGrid"),
  "Image Gallery":      () => import("./ImageGallery"),
  "Scrolling Wall":     () => import("./Marquee"),
  "Highlights":         () => import("./Highlights"),
  "Carousel":           () => import("./Carousel"),
  "Slab":               () => import("./SlabCarousel"),
  "Hero Quote":         () => import("./HeroQuote"),
  "Hero Video":         () => import("./HeroVideo"),
  "Stories":            () => import("./Stories"),
  "Tweet Grid":         () => import("./TweetGrid"),
  "Video Reels":        () => import("./VideoReels"),
  "Audio Wall":         () => import("./AudioWall"),
  "List":               () => import("./List"),
  "Bubble List":        () => import("./BubbleList"),
  "Avatar Strip":       () => import("./AvatarStrip"),
  "Avatar Strip Popup": () => import("./AvatarStripPopup"),
  "Stat Grid":          () => import("./StatGrid"),
  "Rating Badge":       () => import("./RatingBadge"),
  "Logo Bar":           () => import("./LogoBar"),
  "Floating Badge":     () => import("./FloatingBadge"),
  "Floating Toast":     () => import("./FloatingToast"),
  "Press Wall":         () => import("./PressWall"),
  "Tabbed Wall":        () => import("./TabbedWall"),
  "Inline Quote":       () => import("./NotionQuote"),
  "Single Testimonial": () => import("./SingleProof"),
  "Stagger":            () => import("./Stagger"),
};

export const widgetKinds = () => Object.keys(LOADERS);

export const hasWidget = (kind) => Boolean(LOADERS[kind]);

// Loads the component for one kind. Every widget module default-exports its
// component, so there is nothing per-widget to remember here.
//
// Returns { Component, state }, where state is one of:
//   "loading" — the chunk is on its way
//   "ready"   — Component is renderable
//   "unknown" — nothing is registered under this kind
//   "error"   — the chunk failed to load (offline, or a deploy mid-flight)
//
// Callers have to treat "loading" as a real state. The widget page emits a
// modulepreload for its own kind, so in the embed this usually resolves
// within the same paint, but that is a head start, not a guarantee.
export const useWidgetComponent = (kind) => {
  const [Component, setComponent] = useState(null);
  const [state, setState] = useState(() => (LOADERS[kind] ? "loading" : "unknown"));

  useEffect(() => {
    const loader = LOADERS[kind];
    if (!loader) {
      setComponent(null);
      setState("unknown");
      return undefined;
    }

    let cancelled = false;
    setState("loading");
    loader()
      .then((mod) => {
        if (cancelled) return;
        // setState treats a bare function as an updater, hence the wrapper.
        setComponent(() => mod.default);
        setState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setState("error");
      });

    return () => { cancelled = true; };
  }, [kind]);

  return { Component, state };
};
